import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaSearch,
  FaUser,
  FaBars,
  FaTimes,
  FaHeart,
  FaPhone,
  FaWhatsapp,
  FaShoppingBag,
  FaMoon,
  FaSun
} from 'react-icons/fa';

import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { logout } from '../../redux/slices/authSlice';
import { toggleDarkMode } from '../../redux/slices/uiSlice';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [scrolled, setScrolled] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const isAdmin = isAuthenticated && user?.role === 'admin';
  const { wishlist, darkMode } = useSelector((state) => state.ui);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();

    if (searchTerm.trim()) {
      navigate(`/products?search=${searchTerm}`);
      setSearchTerm('');
      setIsOpen(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/products' },
    { name: 'Accessories', path: '/accessories' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <>
      <nav
        className={`fixed w-full z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-white/90 dark:bg-slate-950/90 backdrop-blur-xl border-b border-white/70 dark:border-slate-800 shadow-soft'
            : 'bg-transparent'
        }`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between gap-4 py-4">
            <Link to="/" className="flex items-center gap-2.5 group">
              <img
                src="/logo.png"
                alt="Waziri Mobile Logo"
                className="h-10 w-10 object-contain bg-white rounded-full p-0.5 border border-slate-200 dark:border-slate-800 transition-transform duration-300 group-hover:scale-105"
              />
              <div className="flex flex-col">
                <span className="text-xl font-black tracking-tight bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">
                  Waziri
                </span>
                <span className="text-[10px] font-bold text-slate-900 dark:text-white tracking-widest uppercase -mt-0.5">
                  Mobile
                </span>
              </div>
            </Link>

            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="relative text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-500 transition-colors duration-300"
                >
                  <span className="flex items-center gap-2">
                    {link.icon && <link.icon className="text-base" />}
                    {link.name}
                  </span>

                  {link.badge > 0 && (
                    <span className="absolute -top-2 -right-4 bg-primary-600 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center">
                      {link.badge}
                    </span>
                  )}
                </Link>
              ))}
            </div>

            <form
              onSubmit={handleSearch}
              className="hidden lg:flex items-center gap-2 bg-slate-100 dark:bg-slate-900 rounded-full px-4 py-2 w-96 border border-slate-200 dark:border-slate-700"
            >
              <input
                type="text"
                placeholder="Search phones, accessories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-transparent outline-none flex-1 text-slate-700 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500"
              />
              <button
                type="submit"
                className="text-slate-600 dark:text-slate-400 hover:text-primary-600"
              >
                <FaSearch />
              </button>
            </form>

            <div className="hidden lg:flex items-center gap-5">
              <button
                onClick={() => dispatch(toggleDarkMode())}
                className="text-slate-700 dark:text-slate-300 hover:text-primary-600"
              >
                {darkMode ? <FaSun className="text-xl" /> : <FaMoon className="text-xl" />}
              </button>

              <Link
                to="/wishlist"
                className="relative text-slate-700 dark:text-slate-300 hover:text-primary-600"
              >
                <FaHeart className="text-xl" />
                {wishlist.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center">
                    {wishlist.length}
                  </span>
                )}
              </Link>

              {isAdmin && (
                <div className="relative group">
                  <button className="flex items-center gap-2 text-slate-700 dark:text-slate-300 hover:text-primary-600">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-r from-primary-600 to-primary-800 text-white flex items-center justify-center">
                      {user?.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <span className="hidden xl:inline">{user?.name?.split(' ')[0]}</span>
                  </button>

                  <div className="absolute right-0 mt-2 w-48 rounded-2xl bg-white dark:bg-slate-900 shadow-soft opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 border border-slate-200 dark:border-slate-700">
                    <Link
                      to="/admin/profile"
                      className="block px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      Profile
                    </Link>
                    <Link
                      to="/admin"
                      className="block px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      Dashboard
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full px-4 py-2 text-left text-rose-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden text-2xl text-slate-700 dark:text-slate-300"
            >
              {isOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden bg-white/95 dark:bg-slate-950/95 border-t border-slate-200/50 dark:border-slate-800/50 shadow-soft"
            >
              <div className="container-custom py-5 space-y-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className="flex items-center justify-between py-3 text-slate-700 dark:text-slate-300 hover:text-primary-600 transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      {link.icon && <link.icon />}
                      {link.name}
                    </span>
                    {link.badge > 0 && (
                      <span className="rounded-full bg-primary-600 text-white text-[10px] px-2 py-1">
                        {link.badge}
                      </span>
                    )}
                  </Link>
                ))}

                <form
                  onSubmit={handleSearch}
                  className="flex items-center gap-3 bg-slate-100 dark:bg-slate-900 rounded-full px-4 py-2 border border-slate-200 dark:border-slate-700"
                >
                  <input
                    type="text"
                    placeholder="Search phones..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-transparent outline-none flex-1 text-slate-700 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                  />
                  <button type="submit" className="text-slate-600 dark:text-slate-400 hover:text-primary-600">
                    <FaSearch />
                  </button>
                </form>

                <div className="pt-4 border-t border-slate-200 dark:border-slate-800">
                  {isAdmin ? (
                    <button
                      onClick={handleLogout}
                      className="w-full rounded-full bg-rose-500 text-white py-3 hover:bg-rose-600 transition-all"
                    >
                      Logout
                    </button>
                  ) : null}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

export default Navbar;